<template>
  <div class="paper-manage">
    <div class="wrap">
      <div class="page-hd">
        <div class="page-hd-left">
          <h1>组卷管理</h1>
        </div>
        <div class="stat-chips">
          <div class="stat-chip">
            <div class="num">{{ spaceTotal }}</div>
            <div class="label">研伴空间</div>
          </div>
          <div class="stat-chip">
            <div class="num">{{ paperTotal }}</div>
            <div class="label">已提交试卷</div>
          </div>
        </div>
      </div>

      <div class="page-filters">
        <select class="filter-select" v-model="spaceFilter" @change="onFilterChange" aria-label="按研伴空间筛选">
          <option value="">全部研伴空间</option>
          <option v-for="space in spaces" :key="space.id" :value="space.id">{{ space.name }}</option>
        </select>
        <select class="filter-select" v-model="versionFilter" @change="onFilterChange" aria-label="按试卷版本筛选">
          <option value="">全部</option>
          <option value="2">过程版本</option>
          <option value="1">终版</option>
        </select>
        <input
          class="search-input"
          type="search"
          v-model.trim="keyword"
          placeholder="搜索任务 / 试卷名称 / 提交人"
          autocomplete="off"
          @input="onKeywordInput"
        />
      </div>

      <div class="layout">
        <aside class="panel catalog-panel">
          <div class="catalog-list" v-loading="listLoading">
            <div
              v-for="group in groupedPapers"
              :key="group.id"
              class="catalog-group"
              :class="{ 'is-collapsed': collapsedTasks[group.id] }"
            >
              <button type="button" class="catalog-group-hd" @click="toggleTask(group.id)">
                <i class="el-icon-arrow-down catalog-group-arrow"></i>
                <span class="catalog-group-main">
                  <span class="catalog-group-title">{{ group.name }}</span>
                  <span class="catalog-group-meta">组卷任务 · {{ group.papers.length }} 份试卷</span>
                </span>
              </button>
              <div v-show="!collapsedTasks[group.id]" class="catalog-group-body">
                <button
                  v-for="item in group.papers"
                  :key="item.id"
                  type="button"
                  class="catalog-item"
                  :class="{ 'is-active': isSameId(item.id, activePaperId) }"
                  @click="selectPaper(item)"
                >
                  <div class="item-title-row">
                    <div class="item-title">{{ item.title }}</div>
                    <span v-if="hasVersion(item)" class="version-tag" :class="versionClass(item)">{{ versionLabel(item) }}</span>
                    <span v-if="isFinalPaper(item)" class="review-tag" :class="reviewClass(item)">{{ reviewLabel(item) }}</span>
                  </div>
                  <div class="item-meta">
                    <div class="meta-row">
                      <span class="meta-label">研伴空间</span>
                      <span class="meta-value">{{ item.spxName || '—' }}</span>
                    </div>
                    <div class="meta-row">
                      <span class="meta-label">提交人</span>
                      <span class="meta-value">{{ item.author || '—' }}</span>
                    </div>
                    <div class="meta-row">
                      <span class="meta-label">提交时间</span>
                      <span class="meta-value">{{ formatTime(item.submittedAt) }}</span>
                    </div>
                    <div v-if="isFinalPaper(item)" class="meta-row">
                      <span class="meta-label">专家确认</span>
                      <span class="meta-value">{{ reviewLabel(item) }}<template v-if="reviewCode(item) === 1"> · 可上传答题卡</template></span>
                    </div>
                  </div>
                </button>
              </div>
            </div>
            <div v-if="!listLoading && groupedPapers.length === 0" class="empty">
              <svg viewBox="0 0 72 72" fill="none">
                <rect x="16" y="14" width="40" height="48" rx="4" fill="#e8f0f8" stroke="#b8d0e8" stroke-width="2" />
                <path d="M26 28h20M26 36h16M26 44h12" stroke="#8fb0d0" stroke-width="2" stroke-linecap="round" />
              </svg>
              <div>{{ hasFilters ? '暂无数据' : '暂无已提交试卷' }}</div>
            </div>
          </div>
        </aside>

        <section class="panel detail-panel" v-loading="detailLoading">
          <div class="detail-scroll" v-if="activePaper">
            <div class="paper-detail-layout">
              <div class="paper-card-slot">
              <main class="paper-card" ref="paperMain">
                <header class="paper-head">
                  <h1>{{ activePaper.title }}</h1>
                  <div class="paper-head-meta">
                    <span class="paper-tag">{{ flatQuestions.length }}题</span>
                    <span class="paper-tag" v-if="activePaper.grade">{{ activePaper.grade }}</span>
                  </div>
                </header>

                <div v-for="(section, sIdx) in paperSections" :key="'sec-' + sIdx" class="paper-section">
                  <h2 class="section-title">{{ section.name }}</h2>
                  <article
                    v-for="q in section.questions"
                    :key="q.id"
                    :id="questionDomId(q.id)"
                    class="q-item"
                    :class="{ 'is-active': isSameId(q.id, currentNoteQid) }"
                    @click="selectQuestion(q.id)"
                  >
                    <div class="q-stem">
                      <span class="q-no">{{ q.no }}.</span>
                      <span v-if="q.score != null && q.score !== ''" class="q-score">（{{ q.score }}分）</span>
                      <div class="q-stem-html" v-html="q.stem"></div>
                    </div>
                    <div class="q-dims" :class="{ 'is-collapsed': isDimCollapsed(q.id) }">
                      <button type="button" class="q-dims-hd" @click.stop="toggleDim(q.id)">
                        <span class="q-dims-title">
                          <i class="el-icon-arrow-down q-dims-arrow"></i>
                          <span>细目表维度标记</span>
                        </span>
                        <span v-if="q.markStatus != null" class="q-dims-status" :class="'is-' + q.markStatus">{{ markStatusLabel(q.markStatus) }}</span>
                      </button>
                      <div v-show="!isDimCollapsed(q.id)" class="q-dims-grid">
                        <div v-for="col in markDisplayColumns" :key="col.key" class="q-dim">
                          <span class="q-dim-label">{{ col.label }}</span>
                          <div class="q-dim-box">{{ (q.markTexts && q.markTexts[col.key]) || '—' }}</div>
                        </div>
                      </div>
                    </div>
                    <div v-show="showAnswer" class="q-extra" data-answer-block>
                      <div class="q-extra-row">
                        <span class="q-extra-label">【答案】</span>
                        <span
                          v-if="q.answerParsed || q.answer"
                          class="q-extra-html"
                          v-html="q.answerParsed || q.answer"
                        ></span>
                        <template v-else>—</template>
                      </div>
                      <div class="q-extra-row">
                        <span class="q-extra-label">【解析】</span>
                        <div class="q-extra-html is-block" v-if="q.analysis" v-html="q.analysis"></div>
                        <template v-else>—</template>
                      </div>
                    </div>
                    <div v-if="notesOf(q.id).length" class="q-note-display">
                      <div
                        v-if="notesOf(q.id).length > 1"
                        class="q-note-display-head"
                      >批注（{{ notesOf(q.id).length }} 条）</div>
                      <div
                        v-for="(note, ni) in notesOf(q.id)"
                        :key="note.id || ('n-' + q.id + '-' + ni)"
                        class="q-note-item"
                      >
                        <div class="q-note-display-meta">
                          <span class="q-note-display-author">{{ note.author || '—' }}</span>
                          <span class="q-note-display-time">{{ formatNoteTime(note.time) }}</span>
                          <span v-if="note.versionName" class="q-note-display-version">{{ note.versionName }}</span>
                        </div>
                        <div class="q-note-display-text">{{ note.content }}</div>
                      </div>
                    </div>
                  </article>
                </div>
              </main>
              </div>

              <div class="detail-aside">
                <aside class="side-card">
                  <div class="side-card-hd">
                    <h3>题目导航</h3>
                    <button
                      type="button"
                      class="btn-outline"
                      :class="{ 'is-on': showAnswer }"
                      @click="toggleAnswer"
                    >{{ showAnswer ? '隐藏答案解析' : '显示答案解析' }}</button>
                  </div>
                  <div class="toc-list">
                    <button
                      v-for="q in flatQuestions"
                      :key="'toc-' + q.id"
                      type="button"
                      class="toc-item"
                      :class="{
                        'is-current': isSameId(q.id, currentNoteQid),
                        'has-note': hasAnyNote(q.id)
                      }"
                      @click.stop="selectQuestion(q.id, true)"
                    >{{ q.no }}</button>
                  </div>
                  <div v-if="isFinalPaper(activePaper)" class="paper-review-bar" :class="'is-' + reviewKey(activePaper)">
                    <div class="paper-review-title">
                      <span>终版可行性确认</span>
                      <span class="review-tag" :class="reviewClass(activePaper)">{{ reviewLabel(activePaper) }}</span>
                    </div>
                    <div class="paper-review-desc">{{ reviewDesc(activePaper) }}</div>
                    <div v-if="reviewCode(activePaper) === 2 && activePaper.reviewReason" class="paper-review-reason">
                      <span>原因：</span>{{ activePaper.reviewReason }}
                    </div>
                    <div class="paper-review-actions">
                      <button type="button" class="btn-approve" :disabled="reviewSaving || reviewLocked(activePaper)" @click="confirmFeasible">{{ reviewCode(activePaper) === 1 ? '已确认可行' : '确认可行' }}</button>
                      <button type="button" class="btn-reject" :disabled="reviewSaving || reviewLocked(activePaper)" @click="openReject">{{ reviewCode(activePaper) === 2 ? '已判定不可行' : '不可行' }}</button>
                    </div>
                  </div>
                  <div class="side-stat">共 {{ flatQuestions.length }} 题<span v-if="totalScore"> · 满分 {{ totalScore }} 分</span></div>
                </aside>

                <aside class="note-panel">
                  <div class="note-panel-editor">
                    <div class="note-panel-head">
                      <span class="note-panel-title">第 {{ currentNoteNo || 1 }} 题 · 我的批注</span>
                      <span class="note-panel-status" :class="{ 'is-saved': noteStatusSaved }">{{ noteStatusText }}</span>
                    </div>
                    <textarea
                      class="note-panel-input"
                      v-model="noteDraft"
                      rows="8"
                      placeholder="填写本题批注、易错点或讲解备注…"
                      :disabled="currentNoteQid == null || currentNoteQid === '' || noteSaving"
                      @input="onNoteInput"
                    ></textarea>
                    <div class="note-panel-actions">
                      <button
                        type="button"
                        class="btn-outline"
                        :disabled="noteSaving || currentNoteQid == null || currentNoteQid === ''"
                        @click="clearCurrentNote"
                      >清除</button>
                      <button
                        type="button"
                        class="btn-confirm"
                        :disabled="!canConfirmNote || noteSaving"
                        @click="confirmCurrentNote"
                      >{{ noteSaving ? '保存中...' : '确认批注' }}</button>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </div>

          <div v-else class="detail-placeholder">
            <svg viewBox="0 0 80 80" fill="none">
              <rect x="18" y="12" width="44" height="56" rx="4" fill="#e8f0f8" stroke="#b8d0e8" stroke-width="2" />
              <path d="M28 28h24M28 38h20M28 48h14" stroke="#8fb0d0" stroke-width="2" stroke-linecap="round" />
            </svg>
            <div>{{ detailLoading ? '加载中...' : '请从左侧目录选择一套试卷查看' }}</div>
          </div>
        </section>
      </div>
    </div>

    <el-dialog
      title="判定不可行"
      :visible.sync="rejectVisible"
      width="460px"
      append-to-body
      :close-on-click-modal="false"
    >
      <p class="reject-reason-tip">请填写具体原因。判定不可行后，学院端暂不能上传答题卡，且不能再改。</p>
      <el-input
        v-model="rejectReason"
        type="textarea"
        :rows="5"
        maxlength="500"
        show-word-limit
        placeholder="例如：题量偏少 / 知识点覆盖不足 / 难度梯度不合理"
      ></el-input>
      <span slot="footer">
        <el-button @click="rejectVisible = false">取消</el-button>
        <el-button type="danger" :loading="reviewSaving" @click="submitReject">确认不可行</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'PaperManage',
  data() {
    return {
      spaces: [],
      allPapers: [],
      spaceFilter: '',
      versionFilter: '',
      keyword: '',
      keywordTimer: null,
      listLoading: false,
      detailLoading: false,
      activePaperId: null,
      activePaper: null,
      collapsedTasks: {},
      dimCollapsed: {},
      markSpecMode: 0,
      markSpecColumns: [],
      flatQuestions: [],
      paperSections: [],
      showAnswer: false,
      notesMap: {},
      noteSaving: false,
      currentNoteQid: '',
      currentNoteNo: 0,
      noteDraft: '',
      confirmedDraft: '',
      noteDirty: false,
      rejectVisible: false,
      rejectReason: '',
      reviewSaving: false
    }
  },
  computed: {
    ...mapState({
      userInfo: state => state.user.userInfo,
      currentProject: state => state.user.currentProject
    }),
    currentUserId() {
      const info = this.userInfo || {}
      return info.id != null ? info.id : info.userId
    },
    currentAuthor() {
      const info = this.userInfo || {}
      return info.realName || info.nickname || info.name || '专家'
    },
    spaceTotal() {
      return this.spaces.length
    },
    paperTotal() {
      return this.allPapers.length
    },
    hasFilters() {
      return Boolean(this.spaceFilter || this.versionFilter || this.keyword)
    },
    filteredPapers() {
      const kw = (this.keyword || '').trim().toLowerCase()
      return this.allPapers.filter(item => {
        if (this.spaceFilter !== '' && this.spaceFilter != null && !this.isSameId(item.spaceId, this.spaceFilter)) {
          return false
        }
        if (this.versionFilter === '1' && !this.isFinalPaper(item)) return false
        if (this.versionFilter === '2' && !this.isProcessPaper(item)) return false
        if (kw) {
          const hay = [item.taskName, item.title, item.author, item.spxName].join(' ').toLowerCase()
          if (hay.indexOf(kw) < 0) return false
        }
        return true
      })
    },
    groupedPapers() {
      const order = []
      const map = {}
      this.filteredPapers.forEach(item => {
        const tid = item.taskId != null && item.taskId !== '' ? String(item.taskId) : '_unassigned'
        if (!map[tid]) {
          map[tid] = {
            id: tid,
            name: item.taskName || '未关联组卷任务',
            papers: []
          }
          order.push(tid)
        }
        map[tid].papers.push(item)
      })
      return order.map(id => map[id])
    },
    totalScore() {
      return this.flatQuestions.reduce((sum, q) => sum + (Number(q.score) || 0), 0)
    },
    canConfirmNote() {
      if (this.noteSaving) return false
      if (this.currentNoteQid == null || this.currentNoteQid === '') return false
      return (this.noteDraft || '').trim() !== String(this.confirmedDraft || '').trim()
    },
    noteStatusText() {
      if (this.currentNoteQid == null || this.currentNoteQid === '') return ''
      const draft = (this.noteDraft || '').trim()
      const changed = draft !== String(this.confirmedDraft || '').trim()
      if (changed) return '未确认'
      if (draft) return '已确认'
      return ''
    },
    noteStatusSaved() {
      return this.noteStatusText === '已确认'
    },
    /** specMode 0 标准五列；1 按自建表头展示，题号不重复显示 */
    markDisplayColumns() {
      if (Number(this.markSpecMode) === 1) {
        return (this.markSpecColumns || [])
          .filter(col => col && col.columnName && col.columnName !== '题号' && col.builtinKey !== 'questionNo')
          .map(col => ({ key: col.columnName, label: col.columnName }))
      }
      return [
        { key: 'knowledgeName', label: '知识点' },
        { key: 'keyAbilityName', label: '关键能力' },
        { key: 'subjectAbilityName', label: '学科能力' },
        { key: 'requirement', label: '考察要求' },
        { key: 'objective', label: '测评目标' }
      ]
    }
  },
  created() {
    this.initPage()
  },
  beforeDestroy() {
    if (this.keywordTimer) clearTimeout(this.keywordTimer)
  },
  methods: {
    isSameId(a, b) {
      if (a === null || a === undefined || b === null || b === undefined || a === '' || b === '') return false
      return String(a) === String(b)
    },
    questionDomId(qid) {
      return 'q-' + qid
    },
    noteKey(qid) {
      return String(qid)
    },
    formatTime(value) {
      if (!value) return '—'
      return String(value)
    },
    formatNoteTime(time) {
      if (time == null || time === '') return '—'
      const raw = typeof time === 'number' ? time : String(time).replace(/-/g, '/').replace('T', ' ')
      const d = new Date(raw)
      if (isNaN(d.getTime())) return String(time)
      const pad = n => (n < 10 ? '0' + n : String(n))
      return d.getFullYear() + '/' + pad(d.getMonth() + 1) + '/' + pad(d.getDate()) + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes())
    },
    formatDifficulty(val) {
      if (val == null || val === '') return '—'
      return Number(val).toFixed(2)
    },
    markStatusLabel(status) {
      if (Number(status) === 2) return '已标记'
      if (Number(status) === 1) return '标记中'
      if (Number(status) === 0) return '未标记'
      return ''
    },
    isDimCollapsed(qid) {
      return this.dimCollapsed[qid] !== false
    },
    toggleDim(qid) {
      this.$set(this.dimCollapsed, qid, !this.isDimCollapsed(qid))
    },
    isMyNote(note) {
      if (!note) return false
      if (this.currentUserId != null && note.userId != null) {
        return this.isSameId(note.userId, this.currentUserId)
      }
      return note.author === this.currentAuthor
    },
    normalizeNotesList(raw) {
      if (raw == null || raw === '') return []
      if (Array.isArray(raw)) {
        return raw.filter(n => n && String(n.content || '').trim())
      }
      if (typeof raw === 'string' && raw.trim()) {
        return [{ content: raw, author: '', time: null }]
      }
      if (raw.content && String(raw.content).trim()) return [raw]
      return []
    },
    mapApiNotes(notes) {
      return (Array.isArray(notes) ? notes : []).map(n => ({
        id: n.id,
        questionId: n.questionId,
        userId: n.userId,
        author: n.userName || '',
        content: n.content || '',
        time: n.createTime || null,
        versionName: n.versionName || n.version || ''
      })).filter(n => String(n.content || '').trim())
    },
    getNotesForQuestion(qid) {
      const list = this.normalizeNotesList(this.notesMap[this.noteKey(qid)])
      return list.slice().sort((a, b) => {
        const ta = a.time != null ? new Date(String(a.time).replace(/-/g, '/')).getTime() : Infinity
        const tb = b.time != null ? new Date(String(b.time).replace(/-/g, '/')).getTime() : Infinity
        if (!isNaN(ta) && !isNaN(tb) && ta !== tb) return ta - tb
        return String(a.author || '').localeCompare(String(b.author || ''), 'zh-CN')
      })
    },
    notesOf(qid) {
      return this.getNotesForQuestion(qid)
    },
    hasAnyNote(qid) {
      return this.getNotesForQuestion(qid).length > 0
    },
    getMyNote(qid) {
      return this.getNotesForQuestion(qid).find(n => this.isMyNote(n)) || null
    },
    getMyNoteContent(qid) {
      const mine = this.getMyNote(qid)
      return mine ? String(mine.content || '') : ''
    },
    upsertLocalNote(qid, entry) {
      const key = this.noteKey(qid)
      const notes = { ...this.notesMap }
      const list = this.normalizeNotesList(notes[key]).slice()
      const idx = list.findIndex(n => {
        if (entry.id != null && n.id != null) return this.isSameId(n.id, entry.id)
        return this.isMyNote(n)
      })
      if (idx >= 0) list[idx] = Object.assign({}, list[idx], entry)
      else list.push(entry)
      notes[key] = list
      this.notesMap = notes
    },
    removeLocalNote(qid, noteId) {
      const key = this.noteKey(qid)
      const notes = { ...this.notesMap }
      let list = this.normalizeNotesList(notes[key]).slice()
      if (noteId != null) {
        list = list.filter(n => !this.isSameId(n.id, noteId))
      } else {
        list = list.filter(n => !this.isMyNote(n))
      }
      if (list.length) notes[key] = list
      else delete notes[key]
      this.notesMap = notes
    },
    formatNowTime() {
      const d = new Date()
      const pad = n => (n < 10 ? '0' + n : String(n))
      return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) +
        ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes()) + ':' + pad(d.getSeconds())
    },
    async createNote(qid, content) {
      const res = await this.$Api.Paired.createExamPaperQuestionNote({
        questionId: qid,
        content
      })
      if (!(res && res.code == 200)) {
        throw new Error((res && res.msg) || '新增批注失败')
      }
      this.upsertLocalNote(qid, {
        id: res.data,
        questionId: qid,
        userId: this.currentUserId,
        author: this.currentAuthor,
        content,
        time: this.formatNowTime()
      })
      return res.data
    },
    async updateNote(noteId, qid, content) {
      const res = await this.$Api.Paired.updateExamPaperQuestionNote({
        id: noteId,
        content
      })
      if (!(res && res.code == 200)) {
        throw new Error((res && res.msg) || '修改批注失败')
      }
      this.upsertLocalNote(qid, {
        id: noteId,
        questionId: qid,
        userId: this.currentUserId,
        author: this.currentAuthor,
        content,
        time: this.formatNowTime()
      })
      return true
    },
    async deleteNote(noteId, qid) {
      const res = await this.$Api.Paired.deleteExamPaperQuestionNote({ id: noteId })
      if (!(res && res.code == 200)) {
        throw new Error((res && res.msg) || '删除批注失败')
      }
      this.removeLocalNote(qid, noteId)
      return true
    },
    async persistNote(qid, rawText) {
      if (qid == null || qid === '') return false
      const val = String(rawText || '').trim()
      const mine = this.getMyNote(qid)

      if (val) {
        if (mine && mine.id != null) {
          await this.updateNote(mine.id, qid, val)
        } else {
          await this.createNote(qid, val)
        }
        return true
      }

      if (mine && mine.id != null) {
        await this.deleteNote(mine.id, qid)
      } else if (mine) {
        this.removeLocalNote(qid, mine.id)
      }
      return false
    },
    async confirmCurrentNote() {
      if (this.currentNoteQid == null || this.currentNoteQid === '') return
      if (this.noteSaving) return
      const draft = (this.noteDraft || '').trim()
      if (!draft && !String(this.confirmedDraft || '').trim()) {
        this.$message.warning('请先填写批注内容')
        return
      }
      this.noteSaving = true
      try {
        const saved = await this.persistNote(this.currentNoteQid, this.noteDraft)
        this.confirmedDraft = saved ? draft : ''
        this.noteDraft = this.confirmedDraft
        this.noteDirty = false
        this.$message.success('批注已确认')
      } catch (err) {
        console.log('保存批注失败', err)
        this.$message.error((err && err.message) || '保存批注失败')
      } finally {
        this.noteSaving = false
      }
    },
    clearCurrentNote() {
      if (this.currentNoteQid == null || this.currentNoteQid === '') return
      const hasSaved = !!String(this.getMyNoteContent(this.currentNoteQid) || '').trim()
      const hasDraft = !!(this.noteDraft || '').trim()
      if (!hasSaved && !hasDraft) return
      this.$confirm('确定清除你在第 ' + this.currentNoteNo + ' 题的批注吗？其他人的批注将保留。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        if (this.noteSaving) return
        this.noteSaving = true
        try {
          this.noteDraft = ''
          await this.persistNote(this.currentNoteQid, '')
          this.confirmedDraft = ''
          this.noteDirty = false
          this.$message.success('已清除本人批注')
        } catch (err) {
          console.log('清除批注失败', err)
          this.$message.error((err && err.message) || '清除批注失败')
          this.noteDraft = this.getMyNoteContent(this.currentNoteQid)
          this.confirmedDraft = this.noteDraft
        } finally {
          this.noteSaving = false
        }
      }).catch(() => {})
    },
    mapExamQuestionItem(raw, index) {
      const item = raw || {}
      return {
        id: item.id,
        paperId: item.paperId,
        sectionId: item.sectionId,
        sort: item.sort,
        thirdQuestionId: item.thirdQuestionId,
        typeId: item.typeId,
        type: item.typeName || '题目',
        partName: item.partName || '',
        status: item.status,
        source: item.source || '',
        stem: item.stem || '',
        answer: item.answer || item.answerParsed || '',
        answerParsed: item.answerParsed || '',
        analysis: this.normalizeExplanation(item.explanation),
        difficulty: item.difficulty,
        difficultyLevelId: item.difficultyLevelId,
        difficultyLevelName: item.difficultyLevelName || '',
        knowledgeName: item.knowledgeName || '',
        keyAbilityName: item.keyAbilityName || '',
        subjectAbilityName: item.subjectAbilityName || '',
        requirement: item.requirement || '',
        objective: item.objective || '',
        markStatus: item.markStatus == null || item.markStatus === '' ? null : Number(item.markStatus),
        score: item.totalScore,
        noteCount: item.noteCount || 0,
        notes: this.mapApiNotes(item.notes),
        no: index + 1,
        raw: item
      }
    },
    // 去掉解析里的「分析」分段；【详解】【点睛】等标题标蓝
    normalizeExplanation(html) {
      if (!html) return ''
      let out = String(html)
      out = out.replace(/<div\b[^>]*\bseg-name\s*=\s*["']分析["'][^>]*>[\s\S]*?<\/div>/gi, '')
      out = out.replace(/【分析】[\s\S]*?(?=【详解】|【点睛】|$)/g, '')
      out = out.replace(/【(详解|点睛|解析|答案)】/g, '<span class="q-extra-label">【$1】</span>')
      return out.trim()
    },
    groupByType(questions) {
      const map = {}
      const order = []
      ;(questions || []).forEach(q => {
        const type = q.type || '题目'
        if (!map[type]) {
          map[type] = []
          order.push(type)
        }
        map[type].push(q)
      })
      return order.map(name => ({
        name,
        questions: map[name]
      }))
    },
    buildNotesMapFromQuestions(questions) {
      const map = {}
      ;(questions || []).forEach(q => {
        map[this.noteKey(q.id)] = (q.notes || []).slice()
      })
      return map
    },
    async initPage() {
      this.listLoading = true
      try {
        await this.fetchExpertPaperList(true)
      } finally {
        this.listLoading = false
      }
    },
    flattenExpertPapers(list) {
      const rows = Array.isArray(list) ? list : []
      const grouped = rows.some(item => item && Array.isArray(item.papers))
      if (!grouped) return rows.map(item => this.mapExamPaperItem(item))
      const papers = []
      rows.forEach(task => {
        const taskPapers = Array.isArray(task.papers) ? task.papers : []
        taskPapers.forEach(paper => {
          papers.push(this.mapExamPaperItem(Object.assign({}, paper, {
            taskId: paper.taskId != null && paper.taskId !== '' ? paper.taskId : task.id,
            taskName: paper.taskName || task.name || ''
          })))
        })
      })
      return papers
    },
    mapExamPaperItem(raw) {
      const item = raw || {}
      return {
        id: item.relId,
        relId: item.relId,
        paperId: item.paperId,
        spaceId: item.spxId,
        spxName: item.spxName || '',
        title: item.mainTitle || item.subTitle || '未命名试卷',
        subTitle: item.subTitle || '',
        coverLabel: item.subTitle || '',
        grade: '',
        questionCount: 0,
        submittedAt: item.createTime || item.updateTime || '',
        author: item.submitUserName || item.creatorName || item.authorName || '',
        isFinal: item.isFinal == null || item.isFinal === '' ? null : Number(item.isFinal),
        taskId: item.taskId != null && item.taskId !== '' ? item.taskId : null,
        taskName: item.taskName || '',
        reviewStatus: item.reviewStatus == null || item.reviewStatus === '' ? null : Number(item.reviewStatus),
        reviewReason: item.reviewReason || '',
        reviewUserName: item.reviewUserName || '',
        reviewTime: item.reviewTime || '',
        answerSheetIdentifier: item.answerSheetIdentifier,
        raw
      }
    },
    buildSpacesFromPapers(papers) {
      const map = {}
      ;(papers || []).forEach(item => {
        if (item.spaceId == null || item.spaceId === '') return
        const key = String(item.spaceId)
        if (!map[key]) {
          map[key] = {
            id: item.spaceId,
            name: item.spxName || ('空间' + item.spaceId)
          }
        }
      })
      return Object.keys(map).map(k => map[k])
    },
    async fetchExpertPaperList(autoSelect = false) {
      const projectId = this.currentProject && this.currentProject.id
      if (projectId == null || projectId === '') {
        this.spaces = []
        this.allPapers = []
        this.activePaperId = null
        this.activePaper = null
        this.flatQuestions = []
        this.paperSections = []
        this.$message.warning('缺少项目ID，暂时无法获取试卷')
        return
      }
      try {
        const res = await this.$Api.Paired.getExamPaperListForExpert({ projectId })
        if (!(res && res.code == 200)) {
          this.spaces = []
          this.allPapers = []
          this.activePaperId = null
          this.activePaper = null
          this.flatQuestions = []
          this.paperSections = []
          this.$message.error((res && res.msg) || '获取专家试卷列表失败')
          return
        }
        const list = Array.isArray(res.data) ? res.data : []
        this.allPapers = this.flattenExpertPapers(list)
        this.spaces = this.buildSpacesFromPapers(this.allPapers)
        const visible = this.filteredPapers
        const stillVisible = visible.some(item => this.isSameId(item.id, this.activePaperId))
        if (!stillVisible) {
          if (visible.length) this.selectPaper(visible[0])
          else {
            this.activePaperId = null
            this.activePaper = null
            this.flatQuestions = []
            this.paperSections = []
          }
        } else if (autoSelect && visible.length && !this.activePaper) {
          this.selectPaper(visible[0])
        }
      } catch (err) {
        console.log('获取专家试卷列表失败', err)
        this.spaces = []
        this.allPapers = []
        this.activePaperId = null
        this.activePaper = null
        this.flatQuestions = []
        this.paperSections = []
        this.$message.error('获取专家试卷列表失败')
      }
    },
    onFilterChange() {
      const list = this.filteredPapers
      const stillVisible = list.some(item => this.isSameId(item.id, this.activePaperId))
      if (!stillVisible) {
        if (list.length) this.selectPaper(list[0])
        else {
          this.activePaperId = null
          this.activePaper = null
          this.flatQuestions = []
          this.paperSections = []
        }
      }
    },
    onKeywordInput() {
      if (this.keywordTimer) clearTimeout(this.keywordTimer)
      this.keywordTimer = setTimeout(() => {
        this.onFilterChange()
      }, 200)
    },
    async selectPaper(item) {
      if (!item || item.id == null || item.id === '') return
      if (this.isSameId(item.id, this.activePaperId) && this.activePaper) return
      this.activePaperId = item.id
      this.detailLoading = true
      this.showAnswer = false
      this.currentNoteQid = ''
      this.currentNoteNo = 0
      this.noteDraft = ''
      this.confirmedDraft = ''
      this.noteDirty = false
      try {
        this.activePaper = { ...item }
        const questions = await this.fetchExamPaperQuestionList(item.paperId, item.taskId)
        this.flatQuestions = questions
        this.paperSections = this.groupByType(questions)
        this.notesMap = this.buildNotesMapFromQuestions(questions)
        this.$nextTick(() => {
          if (questions.length) this.selectQuestion(questions[0].id, false, true)
        })
      } finally {
        this.detailLoading = false
      }
    },
    async fetchExamPaperQuestionList(paperId, taskId) {
      if (paperId == null || paperId === '') {
        this.$message.warning('缺少试卷ID，无法加载题目')
        return []
      }
      const params = { paperId }
      if (taskId != null && taskId !== '') params.taskId = taskId
      try {
        const res = await this.$Api.Paired.getExamPaperQuestionList(params)
        if (!(res && res.code == 200)) {
          this.$message.error((res && res.msg) || '获取试卷题目失败')
          return []
        }
        // 新结构：data.questions；兼容旧结构：data 直接为数组
        const raw = res.data
        const list = Array.isArray(raw)
          ? raw
          : (raw && Array.isArray(raw.questions) ? raw.questions : [])
        let questions = list.map((item, index) => this.mapExamQuestionItem(item, index))
        this.markSpecMode = 0
        this.markSpecColumns = []
        if (taskId != null && taskId !== '') {
          const markData = await this.fetchQuestionMarks(taskId, paperId)
          const specMode = markData && Number(markData.specMode) === 1 ? 1 : 0
          const specColumns = markData && Array.isArray(markData.specColumns) ? markData.specColumns : []
          this.markSpecMode = specMode
          this.markSpecColumns = specColumns.slice().sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0))
          questions = this.applyQuestionMarks(questions, markData, specMode)
        } else {
          questions = this.applyQuestionMarks(questions, null, 0)
        }
        return questions
      } catch (err) {
        console.log('[examPaper/question/list] 请求失败', err)
        this.$message.error('获取试卷题目失败')
        return []
      }
    },
    async fetchQuestionMarks(taskId, paperId) {
      try {
        const res = await this.$Api.Paired.getExamComposeQuestionMark({ taskId, paperId })
        if (!(res && res.code == 200)) return null
        return res.data || null
      } catch (err) {
        console.log('[examCompose/questionMark] 请求失败', err)
        return null
      }
    },
    formatMarkValue(value) {
      if (value == null || value === '') return ''
      if (Array.isArray(value)) {
        return value.map(item => (item == null ? '' : String(item).trim())).filter(Boolean).join('、')
      }
      return String(value).trim()
    },
    applyQuestionMarks(questions, markData, specMode) {
      const marks = markData && Array.isArray(markData.questions) ? markData.questions : []
      const map = {}
      marks.forEach(mark => {
        const id = mark && mark.paperQuestionId
        if (id != null && id !== '') map[String(id)] = mark
      })
      const customColumns = Number(specMode) === 1
        ? (this.markSpecColumns || []).filter(col => col && col.columnName && col.columnName !== '题号' && col.builtinKey !== 'questionNo')
        : []
      const standardKeys = ['knowledgeName', 'keyAbilityName', 'subjectAbilityName', 'requirement', 'objective']
      return (questions || []).map(q => {
        const mark = map[String(q.id)] || null
        const markTexts = {}
        if (Number(specMode) === 1) {
          const customMarks = (mark && mark.customMarks) || {}
          customColumns.forEach(col => {
            markTexts[col.columnName] = this.formatMarkValue(customMarks[col.columnName])
          })
        } else {
          standardKeys.forEach(key => {
            const fromMark = mark ? this.formatMarkValue(mark[key]) : ''
            markTexts[key] = fromMark || this.formatMarkValue(q[key])
          })
        }
        return Object.assign({}, q, {
          markTexts,
          markStatus: mark && mark.markStatus != null && mark.markStatus !== '' ? Number(mark.markStatus) : q.markStatus
        })
      })
    },
    toggleAnswer() {
      this.showAnswer = !this.showAnswer
    },
    onNoteInput() {
      this.noteDirty = (this.noteDraft || '').trim() !== String(this.confirmedDraft || '').trim()
    },
    selectQuestion(qid, scroll, force) {
      if (qid == null || qid === '') return
      const meta = this.flatQuestions.find(q => this.isSameId(q.id, qid))
      if (!meta) return

      if (this.isSameId(qid, this.currentNoteQid) && !force) {
        if (scroll) this.scrollToQuestion(qid)
        return
      }

      if (this.noteDirty && this.currentNoteQid !== '' && this.currentNoteQid != null) {
        this.$confirm(
          '第 ' + this.currentNoteNo + ' 题批注尚未确认。<br>确定：先确认并切换<br>取消：放弃修改并切换',
          '提示',
          {
            confirmButtonText: '确认并切换',
            cancelButtonText: '放弃修改',
            type: 'warning',
            dangerouslyUseHTMLString: true,
            distinguishCancelAndClose: true
          }
        ).then(async () => {
          this.noteSaving = true
          try {
            const saved = await this.persistNote(this.currentNoteQid, this.noteDraft)
            this.confirmedDraft = saved ? String(this.noteDraft || '').trim() : ''
            this.noteDraft = this.confirmedDraft
            this.noteDirty = false
            this.doSelectQuestion(qid, scroll)
          } catch (err) {
            console.log('保存批注失败', err)
            this.$message.error((err && err.message) || '保存批注失败')
          } finally {
            this.noteSaving = false
          }
        }).catch(action => {
          if (action === 'cancel') {
            this.noteDirty = false
            this.doSelectQuestion(qid, scroll)
          }
        })
        return
      }

      this.doSelectQuestion(qid, scroll)
    },
    doSelectQuestion(qid, scroll) {
      const meta = this.flatQuestions.find(q => this.isSameId(q.id, qid))
      if (!meta) return
      this.currentNoteQid = qid
      this.currentNoteNo = meta.no
      const val = this.getMyNoteContent(qid)
      this.confirmedDraft = val
      this.noteDraft = val
      this.noteDirty = false
      if (scroll) this.scrollToQuestion(qid)
    },
    scrollToQuestion(qid) {
      this.$nextTick(() => {
        const el = document.getElementById(this.questionDomId(qid))
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
    toggleTask(taskId) {
      this.$set(this.collapsedTasks, taskId, !this.collapsedTasks[taskId])
    },
    hasVersion(item) {
      const value = Number(item && item.isFinal)
      return value === 0 || value === 1 || value === 2
    },
    isFinalPaper(item) {
      return Number(item && item.isFinal) === 1
    },
    isProcessPaper(item) {
      return Number(item && item.isFinal) === 2
    },
    versionLabel(item) {
      const value = Number(item && item.isFinal)
      if (value === 1) return '终版'
      if (value === 2) return '过程版本'
      if (value === 0) return '未终版'
      return ''
    },
    versionClass(item) {
      const value = Number(item && item.isFinal)
      if (value === 1) return 'is-final'
      if (value === 2) return 'is-process'
      return 'is-draft'
    },
    reviewCode(item) {
      if (!this.isFinalPaper(item)) return null
      const value = Number(item && item.reviewStatus)
      if (value === 1 || value === 2) return value
      return 0
    },
    reviewLabel(item) {
      const code = this.reviewCode(item)
      if (code === 1) return '已确认可行'
      if (code === 2) return '不可行'
      return '待确认'
    },
    reviewClass(item) {
      const code = this.reviewCode(item)
      if (code === 1) return 'is-approved'
      if (code === 2) return 'is-rejected'
      return 'is-pending'
    },
    reviewKey(item) {
      const code = this.reviewCode(item)
      if (code === 1) return 'approved'
      if (code === 2) return 'rejected'
      return 'pending'
    },
    reviewDesc(item) {
      const code = this.reviewCode(item)
      if (code === 1) {
        return '已确认可行，学院端可上传答题卡。' + (item.reviewTime ? '确认时间：' + item.reviewTime : '')
      }
      if (code === 2) {
        return '已判定不可行，学院端暂不能上传答题卡。' + (item.reviewTime ? '处理时间：' + item.reviewTime : '')
      }
      return '终版待确认。确认可行后，学院端可上传答题卡。'
    },
    reviewLocked(item) {
      const code = this.reviewCode(item)
      return code === 1 || code === 2
    },
    confirmFeasible() {
      if (!this.activePaper || this.reviewSaving || this.reviewLocked(this.activePaper)) return
      this.$confirm('确认本终版试卷可行？确认后不可再改，学院端可上传答题卡。', '提示', {
        confirmButtonText: '确认可行',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.submitFeasibility(1, '')
      }).catch(() => {})
    },
    openReject() {
      if (!this.activePaper || this.reviewSaving || this.reviewLocked(this.activePaper)) return
      this.rejectReason = ''
      this.rejectVisible = true
    },
    submitReject() {
      const reason = String(this.rejectReason || '').trim()
      if (!reason) {
        this.$message.warning('请填写不可行原因')
        return
      }
      if (reason.length > 500) {
        this.$message.warning('不可行原因不能超过500字')
        return
      }
      this.submitFeasibility(2, reason)
    },
    async submitFeasibility(status, reason) {
      const paper = this.activePaper
      if (!paper || this.reviewLocked(paper)) return
      if (paper.relId == null || paper.relId === '') {
        this.$message.warning('缺少绑定ID，暂时无法提交确认')
        return
      }
      this.reviewSaving = true
      try {
        const query = { relId: paper.relId, status }
        const body = status === 2 ? { reason } : {}
        const res = await this.$Api.Paired.submitExamPaperFeasibility(query, body)
        if (!(res && (res.code == 200 || res.code == 0))) {
          this.$message.error((res && res.msg) || '可行性确认失败')
          return
        }
        this.patchPaperReview(paper.id, status, reason)
        this.rejectVisible = false
        this.$message.success(status === 1 ? '已确认可行' : '已判定不可行')
      } catch (err) {
        console.log('可行性确认失败', err)
        this.$message.error('可行性确认失败')
      } finally {
        this.reviewSaving = false
      }
    },
    patchPaperReview(paperId, status, reason) {
      const pad = n => (n < 10 ? '0' + n : String(n))
      const now = new Date()
      const reviewTime = now.getFullYear() + '-' + pad(now.getMonth() + 1) + '-' + pad(now.getDate()) + ' ' + pad(now.getHours()) + ':' + pad(now.getMinutes())
      const patch = {
        reviewStatus: status,
        reviewReason: status === 2 ? reason : '',
        reviewUserName: this.currentAuthor,
        reviewTime
      }
      this.allPapers = this.allPapers.map(item => (this.isSameId(item.id, paperId) ? Object.assign({}, item, patch) : item))
      if (this.activePaper && this.isSameId(this.activePaper.id, paperId)) {
        this.activePaper = Object.assign({}, this.activePaper, patch)
      }
    }
  }
}
</script>

<style lang="less" scoped>
.thin-scroll() {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.28) transparent;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.28);
    border-radius: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
}

.paper-manage {
  --primary: #4a90e2;
  --primary-dark: #3a7bc8;
  --primary-light: #e8f3ff;
  --primary-blue: #2b7de9;
  --text: #333;
  --text-secondary: #666;
  --text-muted: #999;
  --bg: #fff;
  --card: #fff;
  --border: #e8ecf0;
  --max: 1200px;
  --radius: 8px;
  --shadow: 0 1px 8px rgba(0, 0, 0, 0.06);

  box-sizing: border-box;
  height: auto;
  overflow: visible;
  display: flex;
  flex-direction: column;
  padding: 8px 0 16px;
  background: var(--bg);
  color: var(--text);
  font-size: 14px;
  line-height: 1.5;
  font-family: "PingFang SC", "Microsoft YaHei", "Helvetica Neue", sans-serif;

  * { box-sizing: border-box; }
}

.wrap {
  width: 100%;
  max-width: var(--max);
  margin: 0 auto;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  // padding: 0 16px;
}

.page-hd {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.page-hd-left {
  display: flex;
  align-items: center;

  h1 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: var(--text);
  }
}

.stat-chips {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

.stat-chip {
  background: #fff;
  border-radius: 8px;
  box-shadow: var(--shadow);
  padding: 10px 16px;
  min-width: 96px;
  text-align: center;

  .num {
    font-size: 20px;
    font-weight: 700;
    color: var(--primary);
    line-height: 1.2;
  }

  .label {
    font-size: 12px;
    color: var(--text-muted);
    margin-top: 2px;
  }
}

.layout {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 14px;
  align-items: stretch;
}

.panel {
  background: var(--card);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.page-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  padding: 10px 14px;
  background: #fff;
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  flex-shrink: 0;
}

.filter-select,
.search-input {
  height: 34px;
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 0 12px;
  font-size: 13px;
  font-family: inherit;
  color: var(--text-secondary);
  background: #fafbfc;
  outline: none;

  &:focus {
    border-color: #a8c8f0;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
  }
}

.filter-select {
  width: auto;
  min-width: 168px;
  max-width: 240px;
}

.search-input {
  margin-left: auto;
  width: 280px;
  max-width: 100%;
}

.catalog-panel {
  position: relative;
  min-height: 0;
  overflow: hidden;
}

.catalog-list {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow-x: hidden;
  overflow-y: auto;
  .thin-scroll();
}

.catalog-group {
  border-bottom: 1px solid #eef1f5;

  &:last-child { border-bottom: none; }
}

.catalog-group-hd {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: #f7f9fc;
  text-align: left;
  cursor: pointer;
  font-family: inherit;

  &:hover { background: #eef4fb; }
}

.catalog-group-arrow {
  margin-top: 2px;
  color: var(--text-muted);
  transition: transform 0.15s;
}

.catalog-group.is-collapsed .catalog-group-arrow {
  transform: rotate(-90deg);
}

.catalog-group-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.catalog-group-title {
  font-size: 13px;
  font-weight: 600;
  color: #1a3a5c;
  line-height: 1.4;
}

.catalog-group-meta {
  margin-top: 2px;
  font-size: 11px;
  color: var(--text-muted);
}

.catalog-item {
  padding: 12px 14px 12px 28px;
}

.item-title-row {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 6px 8px;
  margin-bottom: 6px;

  .item-title {
    margin-bottom: 0;
    flex: 1;
    min-width: 0;
  }
}

.version-tag,
.review-tag {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 20px;
  padding: 0 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
}

.version-tag.is-process,
.review-tag.is-pending {
  color: #c47a12;
  background: #fff7e8;
  border: 1px solid #f0d4a0;
}

.version-tag.is-final,
.review-tag.is-approved {
  color: #1f8a4c;
  background: #eaf7ef;
  border: 1px solid #b7e0c6;
}

.version-tag.is-draft {
  color: #666;
  background: #f2f3f5;
}

.review-tag.is-rejected {
  color: #c62828;
  background: #fdeeee;
  border: 1px solid #f0c7c7;
}

.paper-review-bar {
  margin-top: 14px;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #f0d4a0;
  background: #fffaf0;
  min-width: 0;
  max-width: 100%;

  &.is-approved {
    background: #f7fcf9;
    border-color: #b7e0c6;
  }

  &.is-rejected {
    background: #fff8f8;
    border-color: #f0c7c7;
  }
}

.paper-review-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
}

.paper-review-desc,
.paper-review-reason {
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.5;
  overflow-wrap: anywhere;
  word-break: break-all;
}

.paper-review-reason {
  margin-top: 4px;
  color: #a33b3b;
}

.paper-review-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}

.btn-approve,
.btn-reject {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
}

.btn-approve {
  border: none;
  background: #1f8a4c;
  color: #fff;

  &:hover:not(:disabled) { background: #187a42; }

  &:disabled {
    background: #b7e0c6;
    cursor: default;
  }
}

.btn-reject {
  border: 1px solid #e0a0a0;
  background: #fff;
  color: #c62828;

  &:hover:not(:disabled) { background: #fff5f5; }

  &:disabled {
    color: #e0b4b4;
    border-color: #f0d8d8;
    cursor: default;
  }
}

.q-note-display-version::before {
  content: "批注版本：";
}

.reject-reason-tip {
  margin: 0 0 10px;
  color: #666;
  font-size: 13px;
  line-height: 1.5;
}

.catalog-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 12px 14px 12px 28px;
  border: none;
  border-bottom: 1px solid #f3f5f8;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s;
  font-family: inherit;

  &:hover { background: #f7faff; }

  &.is-active {
    background: var(--primary-light);
    position: relative;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: var(--primary);
    }

    .item-title { color: var(--primary); }
  }
}

.item-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
  line-height: 1.4;
  margin-bottom: 6px;
}

.item-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: var(--text-muted);
}

.meta-row {
  display: flex;
  gap: 4px;
  line-height: 1.4;
}

.meta-label {
  color: var(--text-muted);
  flex-shrink: 0;
}

.meta-value {
  color: var(--text-secondary);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empty,
.detail-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  text-align: center;
  color: var(--text-muted);
  font-size: 13px;
  min-height: 360px;

  svg {
    width: 64px;
    height: 64px;
    margin: 0 auto 10px;
    opacity: 0.85;
  }
}

.detail-placeholder svg {
  width: 80px;
  height: 80px;
  margin-bottom: 12px;
  opacity: 0.9;
}

.detail-panel {
  min-height: 0;
  overflow: hidden;
}

.detail-scroll {
  overflow: visible;
}

.paper-detail-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 188px;
  gap: 10px;
  align-items: stretch;
  padding: 8px;
  overflow: visible;
}

.paper-card-slot {
  position: relative;
  min-width: 0;
  min-height: 0;
}

.detail-aside {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: auto;
  min-width: 0;
  min-height: calc(100vh - 280px);
  overflow: visible;
}

.side-card {
  background: #fafbfc;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  padding: 14px 12px;
  flex: 0 0 auto;
  min-width: 0;
  overflow: visible;
}

.side-card-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;

  h3 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #333;
  }
}

.btn-outline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: #fff;
  font-size: 13px;
  color: var(--text-secondary);
  cursor: pointer;
  font-family: inherit;

  &:hover {
    border-color: var(--primary-blue);
    color: var(--primary-blue);
  }

  &.is-on {
    border-color: var(--primary-blue);
    color: var(--primary-blue);
    background: #f0f7ff;
  }
}

.side-card-hd .btn-outline {
  flex-shrink: 0;
  padding: 4px 8px;
  font-size: 12px;
  white-space: nowrap;
}

.toc-list {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
  overflow: visible;
}

.toc-item {
  width: 100%;
  height: 28px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fff;
  font-size: 12px;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
  font-family: inherit;
  padding: 0;

  &:hover {
    border-color: var(--primary-blue);
    color: var(--primary-blue);
  }

  &.is-current,
  &.has-note.is-current {
    border-color: #2b7de9;
    background: #2b7de9;
    color: #fff;
    font-weight: 600;
    box-shadow: none;
  }

  &.has-note {
    border-color: #e0a84a;
    background: #fff;
    color: #666;

    &::after {
      content: "";
      position: absolute;
      top: 3px;
      right: 3px;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: #e0a84a;
    }
  }

  &.is-current.has-note {
    background: #2b7de9;
    color: #fff;
    border-color: #2b7de9;
  }
}

.side-stat {
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
  font-size: 12px;
  color: var(--text-muted);
  line-height: 1.6;
}

.paper-card {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  padding: 16px 18px 22px;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
  .thin-scroll();
}

.paper-head {
  text-align: center;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 20px;

  h1 {
    margin: 0 0 10px;
    font-size: 18px;
    font-weight: 600;
    color: #222;
    line-height: 1.4;
  }
}

.paper-head-meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-bottom: 4px;
}

.paper-tag {
  display: inline-block;
  padding: 3px 10px;
  background: #f0f0f0;
  border-radius: 3px;
  font-size: 12px;
  color: #666;
}

.section-title {
  margin: 24px 0 14px;
  padding-left: 10px;
  border-left: 3px solid var(--primary-blue);
  font-size: 15px;
  font-weight: 600;
  color: #222;

  &:first-of-type { margin-top: 0; }
}

.q-item {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px dashed #eee;
  scroll-margin-top: 12px;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  &.is-active {
    background: #f8fbff;
    margin-left: -10px;
    margin-right: -10px;
    padding-left: 10px;
    padding-right: 10px;
    border-radius: 6px;
    border-bottom-color: transparent;
  }
}

.q-stem {
  font-size: 14px;
  color: #333;
  line-height: 1.75;
  margin-bottom: 10px;
}

.q-no {
  font-weight: 600;
  margin-right: 4px;
}

.q-score {
  color: var(--text-muted);
  font-size: 12px;
  margin-left: 2px;
}

.q-stem-html {
  display: block;
  margin-top: 2px;

  /deep/ .qml-stem {
    display: block;
  }

  /deep/ .qml-stem > p {
    margin: 0 0 8px;
    line-height: 1.75;
  }

  /deep/ .qml-stem > p:last-child {
    margin-bottom: 0;
  }

  /* 选项：对齐原型纵向列表 .q-options */
  /deep/ .qml-og,
  /deep/ div.qml-og {
    margin: 0 0 8px;
    padding: 0;
  }

  /deep/ table.qml-og {
    width: 100%;
    border-collapse: collapse;
    margin: 0;
  }

  /deep/ table.qml-og,
  /deep/ table.qml-og > tbody,
  /deep/ table.qml-og > tbody > tr {
    display: block;
    width: 100%;
  }

  /deep/ table.qml-og td {
    display: block;
    width: 100% !important;
    padding: 4px 0;
    padding-left: 24px;
    color: #444;
    line-height: 1.7;
    vertical-align: top;
  }

  /deep/ .qml-op {
    display: inline;
  }

  /deep/ .xkw-math-img,
  /deep/ img {
    max-width: 100%;
    height: auto;
    vertical-align: middle;
  }

  /deep/ .qml-bk {
    display: inline-block;
    min-width: 4em;
    border-bottom: 1px solid #333;
    text-decoration: none !important;
    color: transparent;
  }
}

.q-dims {
  margin: 8px 0 12px;
  padding: 8px 12px;
  background: #f7f8fa;
  border: 1px solid #eee;
  border-radius: 4px;

  &:not(.is-collapsed) {
    padding: 10px 14px 12px;
    background: #f3faf6;
    border-color: #d7f0e1;
    border-radius: 8px;
  }
}

.q-dims-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
}

.q-dims-title {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: #3d9a62;
}

.q-dims-arrow {
  font-size: 12px;
  transition: transform 0.2s;
}

.q-dims.is-collapsed .q-dims-arrow {
  transform: rotate(-90deg);
}

.q-dims-status {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 500;
  color: #8a8a8a;

  &.is-1 { color: #d48806; }
  &.is-2 { color: #3d9a62; }
}

.q-dims-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 20px;
  margin-top: 12px;
}

.q-dim-label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  color: #666;
}

.q-dim-box {
  min-height: 32px;
  padding: 6px 12px;
  background: #fff;
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  font-size: 13px;
  line-height: 20px;
  color: #333;
  word-break: break-word;
}

.q-kp {
  display: inline-block;
  padding: 1px 8px;
  background: #e8f2fd;
  color: #2b7de9;
  border-radius: 3px;
  font-size: 12px;
}

.q-extra {
  margin-top: 10px;
  padding: 12px 14px;
  background: #f8fafc;
  border-radius: 6px;
  border: 1px solid #eef2f7;
  font-size: 13px;
  color: #333;
}

.q-extra-row {
  margin-bottom: 8px;
  line-height: 1.7;

  &:last-child { margin-bottom: 0; }
}

.q-extra-label {
  color: var(--primary-blue);
  font-weight: 600;
  margin-right: 6px;
}

.q-extra-html {
  /deep/ .qml-answer,
  /deep/ .qml-an-sq,
  /deep/ .qml-an {
    display: inline;
  }

  /deep/ .xkw-math-img,
  /deep/ img {
    max-width: 100%;
    height: auto;
    vertical-align: middle;
  }

  &.is-block {
    display: block;
    margin-top: 4px;

    /deep/ .qml-explanation,
    /deep/ .qml-seg {
      display: block;
      margin: 0 0 8px;
    }

    /deep/ .qml-seg[seg-name="分析"] {
      display: none !important;
    }

    /deep/ .qml-seg:last-child {
      margin-bottom: 0;
    }

    /deep/ .q-extra-label {
      color: var(--primary-blue);
      font-weight: 600;
      margin-right: 6px;
    }

    /deep/ p {
      margin: 0 0 6px;
      line-height: 1.7;
    }
  }
}

.q-note-display {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.q-note-display-head {
  font-size: 12px;
  font-weight: 600;
  color: #8a6d3b;
  padding-left: 2px;
}

.q-note-item {
  padding: 12px 14px;
  background: #fffbf5;
  border: 1px solid #f0e0c8;
  border-left: 3px solid #e0a84a;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.7;
  color: #5c4a32;
}

.q-note-display-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #ead9bc;
  font-size: 12px;
  color: var(--text-muted);
}

.q-note-display-author {
  font-weight: 500;
  color: #8a6d3b;

  &::before {
    content: "批注人：";
    font-weight: 400;
    color: var(--text-muted);
  }
}

.q-note-display-time::before {
  content: "批注时间：";
}

.q-note-display-text {
  color: #5c4a32;
  white-space: pre-wrap;
  word-break: break-word;
}

.note-panel {
  background: #fffbf5;
  border: 1px solid #f0e0c8;
  border-radius: 4px;
  padding: 14px 12px;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  overflow: visible;
}

.note-panel-editor {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.note-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.note-panel-title {
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.note-panel-status {
  font-size: 11px;
  color: var(--text-muted);

  &.is-saved { color: #2e7d32; }
}

.note-panel-input {
  flex: 1 1 auto;
  width: 100%;
  height: auto;
  min-height: 220px;
  border: 1px solid #e8d9c0;
  border-radius: 4px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.65;
  color: #333;
  background: #fff;
  resize: none;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #e0a84a;
    box-shadow: 0 0 0 2px rgba(224, 168, 74, 0.15);
  }

  &::placeholder { color: #c0b09a; }

  &:disabled {
    background: #faf8f5;
    cursor: not-allowed;
  }
}

.note-panel-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
  flex-shrink: 0;

  .btn-outline:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.btn-confirm {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border: none;
  border-radius: 6px;
  background: #e0a84a;
  font-size: 13px;
  color: #fff;
  cursor: pointer;
  font-family: inherit;

  &:hover { background: #d4962f; }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

@media (max-width: 1100px) {
  .paper-detail-layout { grid-template-columns: minmax(0, 1fr); }

  .detail-aside {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: stretch;
    min-height: 0;
    overflow: hidden;
  }

  .note-panel-input { min-height: 220px; }
}

@media (max-width: 900px) {
  .paper-manage {
    height: auto;
    overflow: visible;
  }

  .layout { grid-template-columns: 1fr; }

  .catalog-panel { height: auto; overflow: visible; }

  .catalog-list,
  .paper-card {
    position: static;
    max-height: 420px;
  }

  .stat-chips { display: none; }

  .paper-detail-layout { grid-template-columns: 1fr; }

  .detail-aside {
    grid-template-columns: 1fr;
    position: static;
    max-height: none;
  }

  .paper-card { padding: 16px 14px 22px; }
}
</style>
